import { Client } from "../services/client";
import {
  AddressConfirmStatusEnum,
  DeliveryModeEnum,
  LaptopReplacementStatesEnum,
  OptionValue,
  StringFulBooleanValue,
} from "../helper/constant";
import { filterSameLocations } from "../helper/helper";

export type Asset = {
  assetCategory: string;
  assignedDate: Date;
  modelName: string;
  replacementDate: Date;
  serialNumber: string;
  assignmentType: string;
  isReplaceable: boolean;
  repairTypes: string[];
};

export type HardwareResponse = {
  data: {
    assets: Asset[];
  };
};

type TicketResponse = {
  data: {
    ticketId: string;
    ticketUrl: string;
  };
};

export type ResponseWarp<T> = {
  data: T;
};

export type AssetReplacementsResponse = {
  data: AssetReplacements[];
};

export type ReplacementDetail = {
  mailingAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  contactNumber: string;
  buyBackOption?: OptionValue;
  reconfirmationStatus: AddressConfirmStatusEnum;
};

export type AssetReplacements = {
  state: LaptopReplacementStatesEnum;
  serialNumber: string;
  deliveryMode: DeliveryModeEnum;
  initiatedDate: Date;
  requestId: string;
  isAllCountryLocationShowed: StringFulBooleanValue;
  isBuybackPreferenceEnabled: StringFulBooleanValue;
  allowDeferReplacement: StringFulBooleanValue;
  replacementPeriod?: string;
  details?: ReplacementDetail;
  buyBackLink?: string;
};

type SNLocation = {
  country: string;
  city: string;
  postalCode: string;
  state: string;
  mailingAddress: string;
  title: string;
};

type ContactLocationFromWD = {
  city: string;
  country: string;
  stateISOCode: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  isPrimary: string;
  addressUsage?: string[];
  state: string;
  countryISOCode: string;
  fullAddress: string;
  mailingAddress: string;
};

export type UserLocationsFromSN = {
  allLocationsInHomeOfficeCountry?: SNLocation[];
  workLocationAddress: SNLocation[];
  homeLocationAddress: SNLocation[];
};

export type UserContactInfo = {
  employeeId: string;
  homeAddress: ContactLocationFromWD[];
  personalEmail: string;
  contactInformation: string[];
};

export async function getAssignedHardware(): Promise<Asset[]> {
  try {
    const response = await Client.get<HardwareResponse>("/hardware");
    return response.data.assets;
  } catch (error: any) {
    return [];
  }
}

export async function createZendeskTicket(ticket) {
  return await Client.post<TicketResponse>("/tickets", { ticket: ticket });
}

export async function getAssetReplacement(): Promise<AssetReplacements[]> {
  try {
    const res = await Client.get<ResponseWarp<AssetReplacements[]>>(
      "/assetReplacements"
    );
    return res.data;
  } catch (e) {
    return [];
  }
}

export type UpdateReplacementRequestBody = {
  requestId: string;
  country: string;
  city: string;
  postalCode: string;
  state: string;
  contactNumber: string;
  isBuyOldLaptop: OptionValue;
  mailingAddress: string;
};

export type DeferReplacementRequestBody = {
  requestId: string;
  reason: string;
};

export async function updateAssetReplacementRequest(
  body: UpdateReplacementRequestBody | DeferReplacementRequestBody
): Promise<string> {
  try {
    await Client.put("/assetReplacements", body);
    return "success";
  } catch (e) {
    throw new Error("updateAssetReplacementRequest error");
  }
}

export async function getUserLocations(): Promise<UserLocationsFromSN> {
  try {
    const res = await Client.get<ResponseWarp<UserLocationsFromSN>>(
      `/userLocations?isShowAllCountryLocations=${true}`
    );
    return filterSameLocations(res.data);
  } catch (e) {
    throw new Error(
      "Error while fetching user addresses info from ServiceNow."
    );
  }
}

export async function getUserContactInfo(): Promise<UserContactInfo> {
  try {
    const res = await Client.get<ResponseWarp<UserContactInfo>>(
      "/userContactInfo"
    );
    return res.data;
  } catch (err) {
    throw new Error("Error while fetching user contact info from Workday.");
  }
}
